import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import useNotification from "../../components/notifications/useNotifications";
import CardPaymentModal from "../../components/till/CardPaymentModal";
import CashTenderModal from "../../components/till/CashTenderModal";
import SaleCompleteScreen from "../../components/till/SaleCompleteScreen";
import TillBasketPane from "../../components/till/TillBasketPane";
import TillSearchPane from "../../components/till/TillSearchPane";
import TillTotalsPane, { TillFormValues } from "../../components/till/TillTotalsPane";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import StockTable from "../../components/stock/StockTable";
import Drawer from "../../layouts/Drawer";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useAccount from "../../data/useAccount";
import useBaskets from "../../data/useBaskets";
import useCustomerDetails from "../../data/useCustomerDetails";
import useShippingPolicies from "../../data/useShippingPolicies";
import useTill, { useTillQuote, useTillSale, useTillStockSearch } from "../../data/useTill";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import useReaderDisplaySync from "../../hooks/useReaderDisplaySync";
import useScanToBasket from "../../hooks/useScanToBasket";
import { IAccount } from "../../models/account/IAccount";
import { DiscountType } from "../../models/Basket";
import { IShippingPolicy } from "../../models/ShippingPolicy";
import { ITillQuoteRequest } from "../../models/till/ITillQuote";
import { ITillSale, TillFulfilment, TillTenderMethod } from "../../models/till/ITillSale";
import classNames from "../../utils/classNames";
import { exactTagMatch, looksLikeScan } from "../../utils/till/exactTagMatch";
import { isSaleComplete, needsOperator, shouldPollSale } from "../../utils/till/paymentStatus";
import { isCashEnabled, isTenderable } from "../../utils/till/tenderValidation";
import NoAccess from "../Error/NoAccess";

const READER_KEY = "till.readerId";

type Stage = "selling" | "cash" | "card" | "complete";
type MobileTab = "search" | "basket";

const readStoredReader = (): string | null => {
  try {
    return localStorage.getItem(READER_KEY);
  } catch {
    return null;
  }
};

export default function Till() {
  const { setInfo } = usePageNameContext();
  const { userId, name } = useAuth();
  const { features } = useCustomerDetails();
  const hasTill = useFeatureFlag("Till");

  useEffect(() => {
    setInfo({ name: "Till", desc: "", fill: true });
  }, [setInfo]);

  if (features.data && !hasTill) return <NoAccess />;
  if (!features.data || !userId) return <LoadingWheel />;

  return <TillWorkspace userId={userId} operatorName={name} />;
}

function TillWorkspace({ userId, operatorName }: { userId: string; operatorName?: string }) {
  const { addNotification } = useNotification();
  const { getBasket, addItem, clearBasket } = useBaskets("Till");
  const { config, checkout, cancelSale, retrySale, downloadInvoicePdf, invalidateAfterSale } = useTill("Till");
  const { shippingPolicies } = useShippingPolicies();

  const [stage, setStage] = useState<Stage>("selling");
  const [mobileTab, setMobileTab] = useState<MobileTab>("search");
  const [saleId, setSaleId] = useState<string | undefined>(undefined);
  const [completedSale, setCompletedSale] = useState<ITillSale | undefined>(undefined);
  const [readerId, setReaderId] = useState<string | null>(readStoredReader);
  const [selectedAccount, setSelectedAccount] = useState<IAccount | null>(null);
  const [query, setQuery] = useState("");
  const [pendingEnter, setPendingEnter] = useState(false);
  const [otherConfirm, setOtherConfirm] = useState<TillTenderMethod | null>(null);
  const [stockSearchOpen, setStockSearchOpen] = useState(false);
  const [tenderError, setTenderError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const emptyForm = (shippingPolicyId = ""): TillFormValues => ({
    discountType: DiscountType.Flat, discountValue: "0", shippingPolicyId, notes: "",
    fulfilment: "Collected", line1: "", line2: "", town: "", county: "", postcode: "",
  });

  const { register, setValue, watch, reset } = useForm<TillFormValues>({ defaultValues: emptyForm() });

  const discountType = watch("discountType");
  const discountValueRaw = watch("discountValue");
  const shippingPolicyId = watch("shippingPolicyId");
  const notes = watch("notes");
  const fulfilment: TillFulfilment = watch("fulfilment") === "Delivery" ? "Delivery" : "Collected";
  const address = { line1: watch("line1"), line2: watch("line2"), town: watch("town"), county: watch("county"), postcode: watch("postcode") };

  const policies: IShippingPolicy[] = shippingPolicies.data?.data ?? [];
  const configData = config.data?.data;

  // Defaults from the till configuration, applied once they arrive.
  useEffect(() => {
    if (configData?.defaultShippingPolicyId && !shippingPolicyId) setValue("shippingPolicyId", configData.defaultShippingPolicyId);
  }, [configData?.defaultShippingPolicyId, shippingPolicyId, setValue]);

  const defaultAccount = useAccount(selectedAccount ? undefined : configData?.defaultWalkInAccountId ?? undefined, "Till");
  useEffect(() => {
    if (!selectedAccount && defaultAccount.account.data?.data) setSelectedAccount(defaultAccount.account.data.data);
  }, [defaultAccount.account.data, selectedAccount]);

  useEffect(() => {
    try {
      if (readerId) localStorage.setItem(READER_KEY, readerId);
    } catch {
      /* per-device convenience only */
    }
  }, [readerId]);

  const basketItems = getBasket.data?.data.items;
  const items = useMemo(() => basketItems ?? [], [basketItems]);
  const basketSignature = useMemo(() => items.map((i) => `${i.partId}:${i.basketPrice ?? ""}:${i.taxRate ?? ""}`).join("|"), [items]);
  const inBasketIds = useMemo(() => new Set(items.map((i) => i.partId.toLowerCase())), [items]);

  // A collected sale always uses the free default policy; the chosen one only applies to deliveries.
  const effectivePolicyId = fulfilment === "Delivery" ? shippingPolicyId || null : configData?.defaultShippingPolicyId ?? null;

  // Counter prices are what the customer pays; each part's own VAT treatment (standard, zero or
  // margin scheme) decides how much of that is VAT, so there is no "add VAT" switch here.
  const quoteRequest: ITillQuoteRequest = useMemo(() => ({
    addTaxToTotal: false,
    discountType: discountType === DiscountType.Percentage ? DiscountType.Percentage : DiscountType.Flat,
    discountValue: Math.max(0, Number(discountValueRaw) || 0),
    shippingPolicyId: effectivePolicyId,
  }), [discountType, discountValueRaw, effectivePolicyId]);

  const quote = useTillQuote(quoteRequest, basketSignature, stage === "selling" || stage === "cash" || stage === "card");
  const quoteData = quote.data?.success ? quote.data.data : undefined;
  const quoteError = quote.data && !quote.data.success ? quote.data.message : null;
  const canTender = isTenderable(quoteData) && !quote.isFetching;

  const sale = useTillSale(saleId);
  const saleData = sale.data?.data;
  const selling = stage === "selling";

  useReaderDisplaySync({ readerId, quoteRequest, basketSignature, enabled: selling && !!configData?.stripeEnabled && items.length > 0 });

  // Card sale reached a terminal state. The basket was completed server-side by the poll that
  // saw the payment succeed, so the cached basket is stale until we re-read it.
  useEffect(() => {
    if (stage !== "card" || !saleData) return;
    if (isSaleComplete(saleData.payment.status)) {
      setCompletedSale(saleData);
      setStage("complete");
      invalidateAfterSale();
    }
    // invalidateAfterSale is recreated each render; the transition is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleData, stage]);

  const SEARCH_TAKE = 24;
  const search = useTillStockSearch(query, SEARCH_TAKE);
  const results = search.data?.data;

  // Scanning a part label adds it; vehicle/bin labels are refused rather than navigating away.
  useScanToBasket({
    componentName: "Till",
    onUnhandled: () => addNotification({ variant: "warn", primaryText: "Not a part barcode", secondaryText: "Only part labels can be added to the basket." }),
    onAdded: () => { setQuery(""); searchRef.current?.focus(); },
  });

  const addPart = useCallback((partId: string) => {
    if (inBasketIds.has(partId.toLowerCase())) return;
    addItem.mutate(partId, {
      onSuccess: () => { setQuery(""); searchRef.current?.focus(); },
      onError: () => addNotification({ variant: "error", primaryText: "Couldn't add part" }),
    });
  }, [addItem, inBasketIds, addNotification]);

  // Enter in the search box: an exact tag match adds straight away.
  const submitSearch = useCallback(() => {
    if (looksLikeScan(query)) { setQuery(""); return; }
    if (search.isFetching) { setPendingEnter(true); return; }
    const match = exactTagMatch(results, query);
    if (match) addPart(match.id);
  }, [query, search.isFetching, results, addPart]);

  useEffect(() => {
    if (!pendingEnter || search.isFetching) return;
    setPendingEnter(false);
    const match = exactTagMatch(results, query);
    if (match) addPart(match.id);
  }, [pendingEnter, search.isFetching, results, query, addPart]);

  const buildCheckout = (tender: { method: TillTenderMethod; cashTendered?: number; readerId?: string | null }) => ({
    ...quoteRequest,
    accountId: selectedAccount?.id ?? configData?.defaultWalkInAccountId ?? null,
    notes: notes || undefined,
    fulfilment,
    address: fulfilment === "Delivery" ? address : undefined,
    tender,
  });

  // Delivery needs somewhere to go before any tender is offered.
  const deliveryReady = fulfilment !== "Delivery" || (address.line1.trim().length > 0 && address.postcode.trim().length > 0);
  const guardDelivery = (): boolean => {
    if (deliveryReady) return true;
    addNotification({ variant: "warn", primaryText: "Delivery address needed", secondaryText: "Enter at least the first line and postcode, or switch to Collecting now." });
    return false;
  };

  const finishSale = (saleResult: ITillSale) => {
    setCompletedSale(saleResult);
    setSaleId(saleResult.saleId);
    setStage("complete");
    invalidateAfterSale();
  };

  const takeCash = (tendered: number) => {
    setTenderError(null);
    checkout.mutate(buildCheckout({ method: "Cash", cashTendered: tendered }), {
      onSuccess: (res) => {
        if (!res.success || !res.data) { setTenderError(res.message ?? "The sale could not be completed."); return; }
        finishSale(res.data);
      },
      onError: (e: unknown) => setTenderError(e instanceof Error ? e.message : "The sale could not be completed."),
    });
  };

  const takeOther = (method: TillTenderMethod) => {
    setOtherConfirm(null);
    checkout.mutate(buildCheckout({ method }), {
      onSuccess: (res) => {
        if (!res.success || !res.data) { addNotification({ variant: "error", primaryText: "Sale not completed", secondaryText: res.message ?? undefined }); return; }
        finishSale(res.data);
      },
      onError: (e: unknown) => addNotification({ variant: "error", primaryText: "Sale not completed", secondaryText: e instanceof Error ? e.message : undefined }),
    });
  };

  const startCard = () => {
    setTenderError(null);
    checkout.mutate(buildCheckout({ method: "Card", readerId }), {
      onSuccess: (res) => {
        if (res.data?.saleId) setSaleId(res.data.saleId);
        if (!res.success) setTenderError(res.message ?? "The reader did not take the payment.");
      },
      onError: (e: unknown) => setTenderError(e instanceof Error ? e.message : "The reader did not take the payment."),
    });
  };

  const retryCard = () => {
    if (!saleId) return;
    setTenderError(null);
    retrySale.mutate(saleId, {
      onSuccess: (res) => { if (!res.success) setTenderError(res.message ?? "The reader did not take the payment."); },
    });
  };

  const cancelCard = () => {
    if (!saleId) { setStage("selling"); return; }
    cancelSale.mutate(saleId, {
      onSuccess: (res) => {
        if (res.data && isSaleComplete(res.data.payment.status)) { finishSale(res.data); return; }
        setSaleId(undefined);
        setTenderError(null);
        setStage("selling");
        if (!res.success && res.message) addNotification({ variant: "info", primaryText: res.message });
      },
    });
  };

  const openCash = () => {
    if (!isCashEnabled(configData)) return;
    if (!guardDelivery()) return;
    setTenderError(null);
    setStage("cash");
  };

  const switchToCash = () => {
    if (!isCashEnabled(configData)) return;
    if (!saleId) { setStage("cash"); return; }
    cancelSale.mutate(saleId, {
      onSuccess: (res) => {
        if (res.data && isSaleComplete(res.data.payment.status)) { finishSale(res.data); return; }
        setSaleId(undefined);
        setTenderError(null);
        setStage("cash");
      },
    });
  };

  const newSale = () => {
    // Belt and braces: whatever completed the last sale, start the next one from the server's basket.
    invalidateAfterSale();
    setCompletedSale(undefined);
    setSaleId(undefined);
    setTenderError(null);
    setSelectedAccount(null);
    reset(emptyForm(configData?.defaultShippingPolicyId ?? ""));
    setStage("selling");
    setMobileTab("search");
    window.setTimeout(() => searchRef.current?.focus(), 50);
  };

  if (getBasket.isLoading || config.isLoading) return <LoadingWheel />;

  if (stage === "complete" && completedSale) {
    return (
      <SaleCompleteScreen
        sale={completedSale}
        operatorName={operatorName}
        onNewSale={newSale}
        onDownloadInvoice={() => completedSale.invoiceId ? downloadInvoicePdf(completedSale.invoiceId, `invoice-${completedSale.reference ?? completedSale.saleId}.pdf`) : Promise.resolve()}
      />
    );
  }

  const busy = stage !== "selling" || checkout.isLoading;
  const cardLocked = stage === "card" && (shouldPollSale(saleData?.payment.status) || needsOperator(saleData?.payment.status));

  return (
    <div className="h-full flex flex-col @container">
      <div className="lg:hidden flex border-b border-gray-200 bg-white">
        {(["search", "basket"] as MobileTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setMobileTab(tab)}
            className={classNames("flex-1 h-12 text-sm font-medium", mobileTab === tab ? "text-primary-700 border-b-2 border-primary-600" : "text-gray-500")}
          >
            {tab === "search" ? "Search" : `Basket (${items.length})`}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_1.15fr_22rem]">
        <div className={classNames("min-h-0 @container", mobileTab === "search" ? "block" : "hidden lg:block")}>
          <TillSearchPane
            query={query}
            onQueryChange={setQuery}
            onSubmit={submitSearch}
            results={results}
            take={SEARCH_TAKE}
            isLoading={search.isFetching}
            inBasketIds={inBasketIds}
            onAdd={addPart}
            inputRef={searchRef}
            onOpenFullSearch={() => setStockSearchOpen(true)}
            disabled={busy || cardLocked}
          />
        </div>

        <div className={classNames("min-h-0", mobileTab === "basket" ? "block" : "hidden lg:block")}>
          <TillBasketPane
            items={items}
            quoteLines={quoteData?.lines}
            onClear={() => clearBasket.mutate()}
            isClearing={clearBasket.isLoading}
            disabled={busy || cardLocked}
          />
        </div>

        <div className={classNames("min-h-0 border-t lg:border-t-0 border-gray-200", mobileTab === "basket" ? "block" : "hidden lg:block")}>
          <TillTotalsPane
            quote={quoteData}
            isQuoteLoading={quote.isFetching}
            quoteError={quoteError}
            config={configData}
            register={register}
            setValue={setValue}
            watch={watch}
            selectedAccount={selectedAccount}
            onAccountSelect={setSelectedAccount}
            shippingPolicies={policies}
            canTender={canTender}
            onCash={openCash}
            onCard={() => { if (!guardDelivery()) return; setTenderError(null); setStage("card"); }}
            onOther={(method) => { if (guardDelivery()) setOtherConfirm(method); }}
            disabled={busy || cardLocked}
          />
        </div>
      </div>

      {/* The Stock page's table, filters and all. Its "Add To Basket" action writes to the same basket the till reads. */}
      <Drawer open={stockSearchOpen} setOpen={setStockSearchOpen} title="Stock search" maxWidth="max-w-[96vw]">
        {stockSearchOpen ? <StockTable tableKey="till-stock-search" onAddToBasket={(part) => addPart(part.id)} inBasketIds={inBasketIds} /> : null}
      </Drawer>

      <CashTenderModal
        open={stage === "cash"}
        setOpen={(v) => { if (!v) { setTenderError(null); setStage("selling"); } }}
        total={quoteData?.total ?? 0}
        onConfirm={takeCash}
        isLoading={checkout.isLoading}
        error={tenderError}
      />

      <CardPaymentModal
        open={stage === "card"}
        setOpen={(v) => { if (!v) { setTenderError(null); setSaleId(undefined); setStage("selling"); } }}
        total={quoteData?.total ?? 0}
        config={configData}
        sale={saleData}
        readerId={readerId}
        onReaderChange={setReaderId}
        onStart={startCard}
        onRetry={retryCard}
        onCancel={cancelCard}
        onSwitchToCash={switchToCash}
        isStarting={checkout.isLoading}
        isRetrying={retrySale.isLoading}
        isCancelling={cancelSale.isLoading}
        error={tenderError}
      />

      <ConfirmModal
        open={otherConfirm !== null}
        setOpen={(v: boolean) => { if (!v) setOtherConfirm(null); }}
        title="Paid by bank transfer?"
        message={`This records ${quoteData ? `£${quoteData.total.toFixed(2)}` : "the total"} as received and marks the parts sold. Only confirm once the money is in.`}
        confirmButtonText="Record payment"
        onConfirm={() => otherConfirm && takeOther(otherConfirm)}
        isLoading={checkout.isLoading}
      />
    </div>
  );
}
